import { type Ref, onBeforeUnmount, onMounted } from "vue";

import throttle from "lodash/throttle";

import { ensurePositionWithinViewport } from "@/helpers/elements";

import type { ModalOptions, ResizableEventValues } from "@/models/common";

export const useResizable = (
  options: Ref<Partial<ModalOptions>>,
  draggable: Ref<HTMLElement | null>
) => {
  // Initialize center position immediately if no saved position exists
  if ((options.value.left ?? -1) < 0 || (options.value.top ?? -1) < 0) {
    const { width = 200, height = 200 } = options.value;
    const centerX = (window.innerWidth - width) / 2;
    const centerY = (window.innerHeight - height) / 2;

    options.value = {
      ...options.value,
      left: centerX,
      top: centerY,
      x: centerX,
      y: centerY,
    };
  }

  const repositionAsNeeded = async (force = true) => {
    const { left: currentLeft, top: currentTop } = options.value;

    const updates = await ensurePositionWithinViewport(
      options.value,
      draggable.value?.getBoundingClientRect()
    );

    if (!force && currentLeft === updates.x && currentTop === updates.y) {
      return;
    }

    options.value = {
      ...options.value,
      left: updates.x,
      top: updates.y,
    };
  };

  const updatePosition = async (positionAndEvent: ResizableEventValues) => {
    options.value = {
      ...options.value,
      x: positionAndEvent.left,
      y: positionAndEvent.top,
      width: positionAndEvent.width,
      height: positionAndEvent.height,
    };
    await repositionAsNeeded();
  };

  const resizeListener = throttle(
    async () => {
      await repositionAsNeeded(false);
    },
    100,
    { leading: false }
  );

  onMounted(async () => {
    // Ensure position is properly set and within viewport bounds
    await repositionAsNeeded();
    window.addEventListener("resize", resizeListener);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("resize", resizeListener);
  });

  return {
    updatePosition,
    repositionAsNeeded,
  };
};
